import React, { useEffect, useState, useRef, useCallback } from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './LeafletMap.css';
import { getMapboxToken } from '../../services/api';

const isDevelopment = process.env.NODE_ENV !== 'production';
const devLog = (...args) => {
  if (isDevelopment) {
    console.log(...args);
  }
};
const devWarn = (...args) => {
  if (isDevelopment) {
    console.warn(...args);
  }
};
const devError = (...args) => {
  if (isDevelopment) {
    console.error(...args);
  }
};

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// ========== SIMPLE DOT ICON FUNCTIONS ==========
const createTollgateIcon = (label = '') => {
  const size = 28;
  const innerSize = 20;
  const showLabel = label !== '' && label !== null && label !== undefined;

  const html = `
    <div style="
      position: relative;
      width: ${size}px;
      height: ${size}px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 200;
    ">
      <div style="
        width: ${innerSize}px;
        height: ${innerSize}px;
        background: linear-gradient(135deg, #fac71f, #FF9500);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 2px solid white;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        font-weight: 700;
        color: white;
        font-size: 11px;
      ">
        ${showLabel ? label : ''}
      </div>
    </div>
  `;
  return L.divIcon({
    html,
    className: 'custom-div-icon tollgate-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
};

const createStartIcon = () => {
  return L.divIcon({
    html: `
      <div style="
        position: relative;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        z-index: 200;
      ">
        <div style="
          width: 16px;
          height: 16px;
          background: #34C759;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid white;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        ">
        </div>
      </div>
    `,
    className: 'custom-div-icon start-marker',
    iconSize: [20, 20],
    iconAnchor: [10, 10]
  });
};

const createEndIcon = () => {
  return L.divIcon({
    html: `
      <div style="
        position: relative;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        z-index: 200;
      ">
        <div style="
          width: 16px;
          height: 16px;
          background: var(--secondary);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid white;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        ">
        </div>
      </div>
    `,
    className: 'custom-div-icon end-marker',
    iconSize: [20, 20],
    iconAnchor: [10, 10]
  });
};

const createFuelStationIcon = (brand = 'Fuel') => {
  const palette = {
    Shell: ['#FFCE00', '#E53935'],
    BP: ['#34A853', '#0F9D58'],
    Engen: ['#1565C0', '#1E88E5'],
    Sasol: ['#7B1FA2', '#5E35B1'],
    TotalEnergies: ['#EF6C00', '#E53935'],
    Astron: ['#00897B', '#26A69A'],
    Fuel: ['#1976D2', '#42A5F5'],
  };
  const [primary] = palette[brand] || palette.Fuel;

  return L.divIcon({
    html: `
      <div style="
        position: relative;
        width: 24px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0.92;
      ">
        <div style="
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 22px;
          height: 22px;
          border-radius: 999px;
          background: #1f3b53;
          border: 2px solid white;
          box-shadow: 0 2px 5px rgba(0,0,0,0.18);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M7 4h7a2 2 0 0 1 2 2v12h-2v-5H9v5H7V4Z" fill="white"/>
            <path d="M16 7h2.2A1.8 1.8 0 0 1 20 8.8V14h-2V9h-2V7Z" fill="#dbeafe"/>
          </svg>
        </div>
        <div style="
          position: absolute;
          bottom: 1px;
          left: 50%;
          transform: translateX(-50%);
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: ${primary};
          border: 1.5px solid white;
          box-shadow: 0 1px 3px rgba(0,0,0,0.14);
        "></div>
      </div>
    `,
    className: 'custom-div-icon fuel-station-icon',
    iconSize: [24, 30],
    iconAnchor: [12, 25],
    popupAnchor: [0, -20]
  });
};

// ========== ADDRESS FORMATTING FUNCTION ==========
const formatAddressForPopup = (address) => {
  if (!address) return '';

  const parts = address.split(',').map(part => part.trim());
  const lastThreeParts = parts.slice(-3);

  if (lastThreeParts.length === 0) {
    return `<div>${address}</div>`;
  }

  const town = lastThreeParts[0];
  const province = lastThreeParts.length > 1 ? lastThreeParts[1] : '';
  const country = lastThreeParts.length > 2 ? lastThreeParts[2] : '';

  let html = `<div style="font-weight: 600; font-size: 12px; margin-bottom: 2px;">${town}</div>`;

  if (province) {
    html += `<div style="font-size: 10px; color: #666;">${province}</div>`;
  }

  if (country && country.toLowerCase() !== 'south africa') {
    html += `<div style="font-size: 10px; color: #666;">${country}</div>`;
  }

  return html;
};

// ========== MAIN COMPONENT ==========
const LeafletMap = ({
  routeGeometry = [],
  tollgates = [],
  fuelStations = [],
  height = '400px',
  onTollgateClick = () => { },
  onFuelStationClick = () => { },
  activeTollgate = null,
  activeFuelStation = null,
  startAddress = null,
  endAddress = null,
  autoZoom = true,
}) => {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const polylineRef = useRef(null);
  const startMarkerRef = useRef(null);
  const endMarkerRef = useRef(null);
  const tollMarkersRef = useRef([]);
  const fuelMarkersRef = useRef([]);
  const [mapboxToken, setMapboxToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [popupsOpened, setPopupsOpened] = useState(false);
  const [startEndPopupsDismissed, setStartEndPopupsDismissed] = useState(false);
  const lastRouteHash = useRef('');
  const isInitialMount = useRef(true);
  const suppressStartEndPopupCloseRef = useRef(false);

  // Generate a simple hash for route comparison
  const getRouteHash = (route) => {
    if (!route || route.length === 0) return '';
    const first = route[0];
    const last = route[route.length - 1];
    return `${first[0]},${first[1]}-${last[0]},${last[1]}-${route.length}`;
  };

  // Fetch Mapbox token
  useEffect(() => {
    const fetchMapboxToken = async () => {
      try {
        const data = await getMapboxToken();

        devLog('✅ Mapbox token response received:', {
          hasToken: !!data.mapbox_access_token,
          tokenLength: data.mapbox_access_token?.length
        });

        if (data.mapbox_access_token) {
          setMapboxToken(data.mapbox_access_token);
          devLog('🗺️ Mapbox token set successfully');
        } else {
          devWarn('⚠️ No Mapbox token in response');
          throw new Error('No Mapbox token available');
        }
      } catch (err) {
        devWarn('❌ Could not fetch Mapbox token:', err.message || err);
        setError('Map unavailable - check backend connection');
      } finally {
        setLoading(false);
      }
    };
    fetchMapboxToken();
  }, []);

  // ========== CENTER MAP FUNCTION ==========
  const centerMap = useCallback((force = false) => {
    if (!mapRef.current || routeGeometry.length < 2) return;
    if (!force && hasUserInteracted && !autoZoom) return;

    devLog('🎯 Centering map on route...');

    try {
      const bounds = L.latLngBounds(routeGeometry.map(coord => [coord[0], coord[1]]));
      
      const northEast = bounds.getNorthEast();
      const southWest = bounds.getSouthWest();
      const latDiff = Math.abs(northEast.lat - southWest.lat);
      const lngDiff = Math.abs(northEast.lng - southWest.lng);
      
      let padding = [50, 50];
      if (latDiff < 0.5 && lngDiff < 0.5) {
        padding = [150, 150];
      } else if (latDiff > 10 || lngDiff > 10) {
        padding = [30, 30];
      } else {
        padding = [80, 80];
      }

      mapRef.current.flyToBounds(bounds, {
        padding: padding,
        animate: true,
        duration: 1.0
      });

      devLog('✅ Map centered on route with padding:', padding);
    } catch (err) {
      devError('Failed to center map:', err);
      const bounds = L.latLngBounds(routeGeometry.map(coord => [coord[0], coord[1]]));
      mapRef.current.flyToBounds(bounds, {
        padding: [50, 50],
        animate: true,
        duration: 1.0
      });
    }
  }, [routeGeometry, hasUserInteracted, autoZoom]);

  // ========== MAP INITIALIZATION ==========
  useEffect(() => {
    if (!mapboxToken || !mapContainer.current || loading) return;
    if (mapRef.current) return;

    try {
      const map = L.map(mapContainer.current, {
        zoomControl: true,
        zoomControlOptions: {
          position: 'topright'
        }
      }).setView([-29.0, 24.0], 5);

      mapRef.current = map;

      L.tileLayer(`https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token=${mapboxToken}`, {
        id: 'mapbox/light-v11',
        tileSize: 512,
        zoomOffset: -1,
        accessToken: mapboxToken
      }).addTo(map);

      L.control.scale({ position: 'bottomleft' }).addTo(map);

      const handleUserInteraction = () => {
        if (!hasUserInteracted) {
          devLog('👤 User interacted with map');
          setHasUserInteracted(true);
        }
      };

      map.on('zoomstart', handleUserInteraction);
      map.on('dragstart', handleUserInteraction);

    } catch (err) {
      devError('Map initialization error:', err);
      setError('Failed to initialize map');
    }
  }, [mapboxToken, loading, hasUserInteracted]);

  // ========== CREATE MARKERS WITH PERSISTENT POPUPS ==========
  const createOrUpdateMarkers = useCallback(() => {
    if (!mapRef.current || routeGeometry.length < 2) return;

    try {
      const startPopupContent = `
        <div style="padding: 4px; min-width: 140px; width: auto; font-family: -apple-system, sans-serif;">
          <div style="display: flex; align-items: flex-start; gap: 6px;">
            <div style="
              width: 16px;
              height: 16px;
              border-radius: 50%;
              background: #34C759;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: 600;
              flex-shrink: 0;
              font-size: 9px;
              margin-top: 1px;
            ">
            </div>
            <div style="flex: 1;">
              ${startAddress ? `
                ${formatAddressForPopup(startAddress)}
              ` : '<div style="color: #999; font-style: italic; font-size: 11px;">Address not available</div>'}
            </div>
          </div>
        </div>
      `;

      const endPopupContent = `
        <div style="padding: 4px; min-width: 140px; width: auto; font-family: -apple-system, sans-serif;">
          <div style="display: flex; align-items: flex-start; gap: 6px;">
            <div style="
              width: 16px;
              height: 16px;
              border-radius: 50%;
              background: var(--secondary);
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: 600;
              flex-shrink: 0;
              font-size: 9px;
              margin-top: 1px;
            ">
            </div>
            <div style="flex: 1;">
              ${endAddress ? `
                ${formatAddressForPopup(endAddress)}
              ` : '<div style="color: #999; font-style: italic; font-size: 11px;">Address not available</div>'}
            </div>
          </div>
        </div>
      `;

      // Create or update start marker
      if (!startMarkerRef.current) {
        devLog('📍 Creating start marker');
        startMarkerRef.current = L.marker([routeGeometry[0][0], routeGeometry[0][1]], {
          icon: createStartIcon(),
          title: 'Start Point',
          riseOnHover: true,
          zIndexOffset: 1000
        })
          .bindPopup(startPopupContent, {
            autoClose: false,
            closeOnClick: false,
            closeButton: true,
            autoPan: false,
          })
          .addTo(mapRef.current);
        startMarkerRef.current.on('popupclose', () => {
          if (!suppressStartEndPopupCloseRef.current) {
            setStartEndPopupsDismissed(true);
          }
        });
      } else {
        devLog('📍 Updating start marker position');
        startMarkerRef.current.setLatLng([routeGeometry[0][0], routeGeometry[0][1]]);
      }

      // Create or update end marker
      if (!endMarkerRef.current) {
        devLog('📍 Creating end marker');
        endMarkerRef.current = L.marker([routeGeometry[routeGeometry.length - 1][0], routeGeometry[routeGeometry.length - 1][1]], {
          icon: createEndIcon(),
          title: 'End Point',
          riseOnHover: true,
          zIndexOffset: 999
        })
          .bindPopup(endPopupContent, {
            autoClose: false,
            closeOnClick: false,
            closeButton: true,
            autoPan: false,
          })
          .addTo(mapRef.current);
        endMarkerRef.current.on('popupclose', () => {
          if (!suppressStartEndPopupCloseRef.current) {
            setStartEndPopupsDismissed(true);
          }
        });
      } else {
        devLog('📍 Updating end marker position');
        endMarkerRef.current.setLatLng([routeGeometry[routeGeometry.length - 1][0], routeGeometry[routeGeometry.length - 1][1]]);
      }

    } catch (err) {
      devError('Error creating/updating markers:', err);
    }
  }, [routeGeometry, startAddress, endAddress]);

  // ========== OPEN START/END POPUPS ON FIRST LOAD ==========
  const openStartEndPopups = useCallback(() => {
    if (!mapRef.current || routeGeometry.length < 2 || popupsOpened || startEndPopupsDismissed) return;

    devLog('📍 Opening start and end popups...');

    const tryOpenPopups = () => {
      if (!startMarkerRef.current || !endMarkerRef.current) {
        devLog('❌ Markers not ready, retrying...');
        setTimeout(tryOpenPopups, 300);
        return;
      }

      if (!mapRef.current.hasLayer(startMarkerRef.current) ||
        !mapRef.current.hasLayer(endMarkerRef.current)) {
        devLog('❌ Markers not on map, retrying...');
        setTimeout(tryOpenPopups, 300);
        return;
      }

      devLog('✅ Opening start and end popups');
      try {
        startMarkerRef.current.openPopup();
        endMarkerRef.current.openPopup();
        setPopupsOpened(true);
      } catch (err) {
        devError('Failed to open popups:', err);
      }
    };

    setTimeout(tryOpenPopups, 800);
  }, [routeGeometry, popupsOpened, startEndPopupsDismissed]);

  // ========== AUTO-ZOOM ON INITIAL LOAD ==========
  useEffect(() => {
    if (!mapRef.current || loading || !autoZoom) return;
    
    if (routeGeometry.length > 1) {
      devLog('🚀 Initial route available, auto-zooming...');
      
      const timer = setTimeout(() => {
        if (!hasUserInteracted) {
          centerMap();
        }
      }, 800);
      
      return () => clearTimeout(timer);
    }
  }, [loading, routeGeometry, hasUserInteracted, autoZoom, centerMap]);

  // ========== AUTO-ZOOM ON ROUTE CHANGE ==========
  useEffect(() => {
    if (!mapRef.current || loading || !autoZoom || routeGeometry.length < 2) return;
    
    const currentHash = getRouteHash(routeGeometry);
    
    if (currentHash !== lastRouteHash.current) {
      devLog('🔄 Route changed, auto-zooming...');
      lastRouteHash.current = currentHash;
      
      setHasUserInteracted(false);
      
      const timer = setTimeout(() => {
        centerMap();
      }, 400);
      
      return () => clearTimeout(timer);
    }
  }, [routeGeometry, loading, autoZoom, centerMap]);

  // ========== MAP UPDATE (ROUTE, TOLLGATES) ==========
  useEffect(() => {
    if (!mapRef.current || loading) return;

    try {
      // Clear previous elements (but keep start and end markers)
      if (polylineRef.current) {
        mapRef.current.removeLayer(polylineRef.current);
        polylineRef.current = null;
      }

      // Clear all tollgate markers
      tollMarkersRef.current.forEach(marker => {
        if (marker) mapRef.current.removeLayer(marker);
      });
      tollMarkersRef.current = [];

      // Clear all fuel station markers
      fuelMarkersRef.current.forEach(marker => {
        if (marker) mapRef.current.removeLayer(marker);
      });
      fuelMarkersRef.current = [];

      // Add new route if we have geometry
      if (routeGeometry.length > 1) {
        const latLngs = routeGeometry.map(coord => [coord[0], coord[1]]);

        // Add route polyline
        polylineRef.current = L.polyline(latLngs, {
          color: '#fac71f',
          weight: 5,
          opacity: 0.8,
          lineJoin: 'round'
        }).addTo(mapRef.current);

        // Create or update markers
        createOrUpdateMarkers();

// Add tollgate markers
if (tollgates && tollgates.length > 0) {
  // Use the total route distance from parent OR calculate as fallback
  const totalRouteDistance = tollgates[0]?.totalRouteDistance || 
    (() => {
      // Fallback calculation only if parent didn't provide
      if (routeGeometry.length < 2) return 0;
      let total = 0;
      for (let i = 1; i < routeGeometry.length; i++) {
        const lat1 = routeGeometry[i - 1][0];
        const lon1 = routeGeometry[i - 1][1];
        const lat2 = routeGeometry[i][0];
        const lon2 = routeGeometry[i][1];
        const R = 6371;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                  Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                  Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        total += R * c;
      }
      return total;
    })();
  
  let cumulativeTollCost = 0;
  const fuelConsumptionL100km = 8.0;
  const fuelPricePerLiter = 21.41;

  tollgates.forEach((tollgate, index) => {
    cumulativeTollCost += tollgate.price;
    
    // USE THE PRE-CALCULATED DISTANCE FROM PARENT
    const distanceFromStart = tollgate.distanceFromStart !== null 
      ? tollgate.distanceFromStart
      : Math.round(totalRouteDistance * ((index + 1) / tollgates.length));
    
    const estimatedFuelCost = (fuelConsumptionL100km / 100) * distanceFromStart * fuelPricePerLiter;
    const tollCostSoFar = cumulativeTollCost;
    const totalCostSoFar = estimatedFuelCost + tollCostSoFar;

    const seq = tollgate.sequence ?? (index + 1);

            const markerIcon = createTollgateIcon(seq);

            const marker = L.marker([tollgate.latitude, tollgate.longitude], {
              icon: markerIcon,
              title: `${seq}. ${tollgate.name}`,
              zIndexOffset: 500 + index
            });

    const popupHtml = `
<div style="padding: 8px; min-width: 200px; font-family: -apple-system, sans-serif;">
  <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
    <div style="
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: linear-gradient(135deg, #fac71f, #FF9500);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: 600;
      flex-shrink: 0;
      font-size: 10px;
    ">
      ${seq}
    </div>
    <div>
      <div style="font-size: 13px; font-weight: 600;">${tollgate.name}</div>
      <div style="font-size: 10px; color: #666;">Tollgate ${seq} of ${tollgates.length}</div>
    </div>
  </div>
  
  <div style="margin-top: 8px; border-top: 1px solid #f0f0f0; padding-top: 8px;">
    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
      <span style="font-size: 11px; color: #666;">Distance from start:</span>
      <span style="font-size: 11px; font-weight: 600;">
        ~${Math.round(distanceFromStart)} km
        ${tollgate.distanceFromStart !== null ? '(route distance)' : '(estimated)'}
      </span>
    </div>
  </div>
  
  ${tollgate.progressPercentage !== null ? `
    <div style="margin-top: 4px; margin-bottom: 8px;">
      <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
        <span style="font-size: 11px; color: #666;">Trip progress:</span>
        <span style="font-size: 11px; font-weight: 600;">${tollgate.progressPercentage}%</span>
      </div>
      <div style="width: 100%; height: 4px; background: #e0e0e0; border-radius: 2px; overflow: hidden;">
        <div style="width: ${tollgate.progressPercentage}%; height: 100%; background: linear-gradient(90deg, #34C759, #fac71f);"></div>
      </div>
    </div>
  ` : ''}

    <div style="margin-top: 6px; background: #f8f9fa; padding: 6px; border-radius: 4px;">
      <div style="font-size: 10px; font-weight: 600; color: #666; margin-bottom: 2px; text-transform: uppercase;">Cost breakdown so far:</div>
      
      <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
        <span style="font-size: 11px; color: #666;">Fuel cost:</span>
        <span style="font-size: 11px; font-weight: 600; color: #3498db;">R${estimatedFuelCost.toFixed(2)}</span>
      </div>
      
      <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
        <span style="font-size: 11px; color: #666;">Toll cost:</span>
        <span style="font-size: 11px; font-weight: 600; color: #e67e22;">R${tollCostSoFar.toFixed(2)}</span>
      </div>
      
      <div style="display: flex; justify-content: space-between; margin-top: 4px; padding-top: 4px; border-top: 1px solid #e0e0e0;">
        <span style="font-size: 11px; color: #666; font-weight: 600;">Total so far:</span>
        <span style="font-size: 11px; font-weight: 700; color: #27ae60;">R${totalCostSoFar.toFixed(2)}</span>
      </div>
    </div>

    <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #f0f0f0; font-size: 10px; color: #999;">
      ${index === 0 ? 'First tollgate on route' :
                index === tollgates.length - 1 ? 'Final tollgate' :
                  `${seq}/${tollgates.length} passed`}
    </div>
</div>
`;

            marker.bindPopup(popupHtml);

            marker.on('click', () => {
              onTollgateClick(tollgate, seq);
            });

            marker.addTo(mapRef.current);
            tollMarkersRef.current.push(marker);
          });
        }

        if (fuelStations && fuelStations.length > 0) {
          fuelStations.forEach((station, index) => {
            const marker = L.marker([station.latitude, station.longitude], {
              icon: createFuelStationIcon(station.brand),
              title: station.name,
              zIndexOffset: 450 + index
            });

            const popupHtml = `
              <div style="padding: 8px; min-width: 210px; font-family: -apple-system, sans-serif;">
                <div style="display:flex; justify-content:space-between; gap:8px; align-items:flex-start; margin-bottom:8px;">
                  <div>
                    <div style="font-size:13px; font-weight:700; color:#1f2937;">${station.name}</div>
                    <div style="font-size:11px; color:#1976d2; font-weight:600; margin-top:2px;">${station.brand}</div>
                  </div>
                  <div style="font-size:11px; color:#4caf50; font-weight:700;">${station.distanceFromStart} km</div>
                </div>
                <div style="font-size:11px; color:#5f6c86; line-height:1.4;">${station.address || 'South Africa'}</div>
                <div style="display:flex; gap:10px; flex-wrap:wrap; margin-top:8px; font-size:11px; color:#5f6c86;">
                  <span>Detour: ${station.detourKm} km</span>
                  <span>To destination: ${station.distanceToDestination} km</span>
                </div>
                ${station.services?.length ? `
                  <div style="margin-top:8px; display:flex; gap:6px; flex-wrap:wrap;">
                    ${station.services.map((service) => `<span style="font-size:10px; background:#eef5ff; color:#1d4ed8; padding:3px 6px; border-radius:999px;">${service}</span>`).join('')}
                  </div>
                ` : ''}
              </div>
            `;

            marker.bindPopup(popupHtml);
            marker.on('click', () => onFuelStationClick(station, index));
            marker.addTo(mapRef.current);
            fuelMarkersRef.current.push(marker);
          });
        }
      }

    } catch (err) {
      devError('Map update error:', err);
      setError('Failed to update map');
    }
  }, [routeGeometry, tollgates, fuelStations, onTollgateClick, onFuelStationClick, loading, createOrUpdateMarkers]);

  // ========== OPEN POPUPS ON FIRST ROUTE LOAD ==========
  useEffect(() => {
    if (!mapRef.current || loading || routeGeometry.length < 2) return;

    // Only open popups on first mount
    if (isInitialMount.current) {
      devLog('📍 First mount, setting up popups...');
      
      const timer = setTimeout(() => {
        openStartEndPopups();
        isInitialMount.current = false;
      }, 1200);
      
      return () => clearTimeout(timer);
    }
  }, [routeGeometry, loading, openStartEndPopups]);

  useEffect(() => {
    setStartEndPopupsDismissed(false);
  }, [routeGeometry]);

  // ========== HANDLE TOLLGATE SELECTION - HIDE START/END POPUPS ==========
  useEffect(() => {
    if (!mapRef.current || loading || !tollMarkersRef.current.length) return;

    devLog(`🔄 Active tollgate changed: ${activeTollgate}`);

    // Close all tollgate popups first
    tollMarkersRef.current.forEach(marker => {
      if (marker && marker.closePopup) {
        marker.closePopup();
      }
    });

    // If a tollgate is selected, close start/end popups and open tollgate popup
    if (activeTollgate !== null && activeTollgate < tollMarkersRef.current.length) {
      const marker = tollMarkersRef.current[activeTollgate];
      if (marker && marker.openPopup) {
        devLog(`📢 Opening popup for tollgate ${activeTollgate} (no zoom)`);
        
        // Close start and end popups
        suppressStartEndPopupCloseRef.current = true;
        if (startMarkerRef.current && startMarkerRef.current.isPopupOpen()) {
          startMarkerRef.current.closePopup();
        }
        if (endMarkerRef.current && endMarkerRef.current.isPopupOpen()) {
          endMarkerRef.current.closePopup();
        }
        setTimeout(() => {
          suppressStartEndPopupCloseRef.current = false;
        }, 0);
        
        // Open tollgate popup
        marker.openPopup();
      }
    } else if (!startEndPopupsDismissed) {
      // If no tollgate selected, re-open start and end popups
      setTimeout(() => {
        if (startMarkerRef.current && !startMarkerRef.current.isPopupOpen()) {
          startMarkerRef.current.openPopup();
        }
        if (endMarkerRef.current && !endMarkerRef.current.isPopupOpen()) {
          endMarkerRef.current.openPopup();
        }
      }, 300);
    }
  }, [activeTollgate, loading, startEndPopupsDismissed]);

  useEffect(() => {
    if (!mapRef.current || loading || !fuelMarkersRef.current.length) return;

    fuelMarkersRef.current.forEach((marker) => {
      if (marker?.closePopup) marker.closePopup();
    });

    if (activeFuelStation !== null && activeFuelStation < fuelMarkersRef.current.length) {
      const marker = fuelMarkersRef.current[activeFuelStation];
      if (marker?.openPopup) {
        marker.openPopup();
      }
    }
  }, [activeFuelStation, loading]);

  // ========== UPDATE POPUP CONTENT WHEN ADDRESS CHANGES ==========
  useEffect(() => {
    if (!startMarkerRef.current || !endMarkerRef.current) return;

    const startPopupContent = `
      <div style="padding: 4px; min-width: 140px; width: auto; font-family: -apple-system, sans-serif;">
        <div style="display: flex; align-items: flex-start; gap: 6px;">
          <div style="
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #34C759;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: 600;
            flex-shrink: 0;
            font-size: 9px;
            margin-top: 1px;
          ">
          </div>
          <div style="flex: 1;">
            ${startAddress ? `
              ${formatAddressForPopup(startAddress)}
            ` : '<div style="color: #999; font-style: italic; font-size: 11px;">Address not available</div>'}
          </div>
        </div>
      </div>
    `;

    const endPopupContent = `
      <div style="padding: 4px; min-width: 140px; width: auto; font-family: -apple-system, sans-serif;">
        <div style="display: flex; align-items: flex-start; gap: 6px;">
          <div style="
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: var(--secondary);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: 600;
            flex-shrink: 0;
            font-size: 9px;
            margin-top: 1px;
          ">
          </div>
          <div style="flex: 1;">
            ${endAddress ? `
              ${formatAddressForPopup(endAddress)}
            ` : '<div style="color: #999; font-style: italic; font-size: 11px;">Address not available</div>'}
          </div>
        </div>
      </div>
    `;

    startMarkerRef.current.setPopupContent(startPopupContent);
    endMarkerRef.current.setPopupContent(endPopupContent);

    // Re-open popups if they were open and we're not currently showing a tollgate
    if (popupsOpened && activeTollgate === null && !startEndPopupsDismissed) {
      setTimeout(() => {
        if (startMarkerRef.current && !startMarkerRef.current.isPopupOpen()) {
          startMarkerRef.current.openPopup();
        }
        if (endMarkerRef.current && !endMarkerRef.current.isPopupOpen()) {
          endMarkerRef.current.openPopup();
        }
      }, 100);
    }
  }, [startAddress, endAddress, popupsOpened, activeTollgate, startEndPopupsDismissed]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // ========== RESET VIEW HANDLER ==========
  const handleResetView = () => {
    devLog('🔄 User requested reset view');
    setHasUserInteracted(false);
    setStartEndPopupsDismissed(false);
    centerMap(true);
    
    // Re-open start and end popups
    setTimeout(() => {
      if (startMarkerRef.current && !startMarkerRef.current.isPopupOpen()) {
        startMarkerRef.current.openPopup();
      }
      if (endMarkerRef.current && !endMarkerRef.current.isPopupOpen()) {
        endMarkerRef.current.openPopup();
      }
    }, 500);
  };

  // ========== RENDER ==========
  if (loading) {
    return (
      <div className="leaflet-map loading" style={{ height: height }}>
        <div className="spinner"></div>
        <p>Loading map...</p>
      </div>
    );
  }

  if (error || !mapboxToken) {
    return (
      <div className="leaflet-map error" style={{ height: height }}>
        <FaExclamationTriangle size={48} />
        <h3>Map Unavailable</h3>
        <p>{error || 'Mapbox token not available'}</p>
        <div style={{ fontSize: '14px', marginTop: '10px', color: '#666' }}>
          <p>Make sure:</p>
          <ol style={{ textAlign: 'left', marginTop: '8px' }}>
            <li>FastAPI is running (python main.py)</li>
            <li>Backend has valid Mapbox token in secrets.json</li>
            <li>Backend endpoint /get_mapbox_token is accessible</li>
          </ol>
        </div>
      </div>
    );
  }

  return (
    <div className="leaflet-map" style={{ position: 'relative', height: height }}>
      <div
        ref={mapContainer}
        style={{
          height: '100%',
          width: '100%',
          borderRadius: '8px',
          overflow: 'hidden'
        }}
      />
      
      {/* Center/Refocus Button */}
      {routeGeometry.length > 1 && !loading && !error && mapboxToken && (
        <div className="map-controls">
          <button 
            className="map-center-button"
            onClick={handleResetView}
            title="Center map on route"
            aria-label="Center map on route"
          >
            <svg 
              className="center-button-icon" 
              viewBox="0 0 24 24" 
              width="24" 
              height="24"
              fill="currentColor"
            >
              <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>
            </svg>
          </button>
          {hasUserInteracted && (
            <div className="map-hint">
              Click to re-center on route
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LeafletMap;
